// unifiedNodeTabKeys.js

export const UNIFIED_TAB_KEYS = Object.freeze({
  ABOUT: '__ABOUT__',
  MISSIONS: '__MISSIONS__',
  PORTALS: '__PORTALS__',
  HIGHLIGHT: '__HIGHLIGHT__',
});

export const ADD_MODAL_SPECIAL_KEYS = Object.freeze({
  MISSION: '__ADD_MISSION__',
  PORTAL: '__ADD_PORTAL__',
});

const SPECIAL_TAB_KEY_SET = new Set(Object.values(UNIFIED_TAB_KEYS));

export const UNIFIED_TAB_META = Object.freeze({
  [UNIFIED_TAB_KEYS.ABOUT]: {
    iconKey: 'info',
    description: 'Metadata and details for this record.',
  },
  [UNIFIED_TAB_KEYS.MISSIONS]: {
    iconKey: 'rocket_launch',
    description: 'Workflows and guided processes for this record.',
  },
  [UNIFIED_TAB_KEYS.PORTALS]: {
    iconKey: 'captive_portal',
    description: 'Shared pages for clients, contractors, and collaborators.',
  },
});

/**
 * Returns true when the tab key represents a related node type (not a reserved unified tab).
 *
 * @param {string|null|undefined} tabKey
 * @returns {boolean}
 */
export function isRelatedTypeTabKey(tabKey) {
  const normalized = typeof tabKey === 'string' ? tabKey.trim() : '';
  if (!normalized) return false;
  return !SPECIAL_TAB_KEY_SET.has(normalized);
}

/**
 * Returns true when the tab key is one of the reserved unified tab keys.
 *
 * @param {string|null|undefined} tabKey
 * @returns {boolean}
 */
export function isUnifiedSpecialTabKey(tabKey) {
  const normalized = typeof tabKey === 'string' ? tabKey.trim() : '';
  if (!normalized) return false;
  return SPECIAL_TAB_KEY_SET.has(normalized);
}

const SESSION_TAB_STORAGE_PREFIX = 'nodal:nodeTab:';

/**
 * Reads the persisted tab key for a node from session storage.
 *
 * @param {string|null|undefined} nodeKey
 * @returns {string|null}
 */
export function getSessionTabKey(nodeKey) {
  if (!nodeKey) return null;

  try {
    const value = sessionStorage.getItem(`${SESSION_TAB_STORAGE_PREFIX}${nodeKey}`);
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Persists the active tab key for a node in session storage.
 *
 * @param {string|null|undefined} nodeKey
 * @param {string|null|undefined} tabKey
 */
export function setSessionTabKey(nodeKey, tabKey) {
  if (!nodeKey || !tabKey) return;

  try {
    sessionStorage.setItem(`${SESSION_TAB_STORAGE_PREFIX}${nodeKey}`, tabKey);
  } catch {
    // Ignore storage failures.
  }
}

/**
 * Resolves the default unified tab key for a node visit.
 *
 * Priority after session restore and highlight/mobile exceptions:
 * 1. Missions when mission templates exist for the type
 * 2. First related type tab when related items exist
 * 3. Missions fallback
 *
 * @param {object} params
 * @param {boolean} params.isMobile
 * @param {object|null} params.nodeType
 * @param {string[]} params.visibleTypeKeys
 * @param {boolean} params.hasMissionTemplates
 * @param {string|null} params.sessionTabKey
 * @param {boolean} [params.includeAboutTab] - Include About in available tabs (e.g. peek panel on desktop).
 * @param {boolean} [params.preferAboutTab] - Default to About when no valid session tab is stored.
 * @returns {string}
 */
export function resolveDefaultUnifiedTabKey({
  isMobile,
  nodeType,
  visibleTypeKeys = [],
  hasMissionTemplates,
  sessionTabKey,
  includeAboutTab = false,
  preferAboutTab = false,
}) {
  const availableKeys = [];

  if (isMobile || includeAboutTab) {
    availableKeys.push(UNIFIED_TAB_KEYS.ABOUT);
  }

  availableKeys.push(UNIFIED_TAB_KEYS.MISSIONS, UNIFIED_TAB_KEYS.PORTALS);

  if (nodeType?.showHighlight) {
    availableKeys.push(UNIFIED_TAB_KEYS.HIGHLIGHT);
  }

  availableKeys.push(...visibleTypeKeys);

  if (sessionTabKey && availableKeys.includes(sessionTabKey)) {
    return sessionTabKey;
  }

  if (preferAboutTab && (isMobile || includeAboutTab)) {
    return UNIFIED_TAB_KEYS.ABOUT;
  }

  if (nodeType?.showHighlight) {
    return UNIFIED_TAB_KEYS.HIGHLIGHT;
  }

  if (isMobile) {
    return UNIFIED_TAB_KEYS.ABOUT;
  }

  if (hasMissionTemplates) {
    return UNIFIED_TAB_KEYS.MISSIONS;
  }

  if (visibleTypeKeys[0]) {
    return visibleTypeKeys[0];
  }

  return UNIFIED_TAB_KEYS.MISSIONS;
}
